import { Link } from "react-router-dom";

/**
 * Terms and privacy, written to describe what the site actually does rather
 * than to look like a legal document. Both are short because the service is
 * small: no payments, no adverts, no tracking, and only the data you type.
 */

export function Terms() {
  return (
    <div className="page narrow legal">
      <h1 className="serif hero-small">Terms</h1>
      <p className="muted small">Last updated 24 August 2026</p>

      <h2 className="section-heading serif">What this is</h2>
      <p>
        Everychord works out the chords of a recording and lines them up with its
        words, so you can play along. It is a personal project run for a small
        number of people. There is no charge, no advertising, and no guarantee it
        will keep running.
      </p>

      <h2 className="section-heading serif">The chords are a machine's opinion</h2>
      <p>
        Chords are detected by a model listening to audio. They are frequently
        close and sometimes wrong, and anyone signed in can correct them. Nothing
        here is an authoritative transcription, and none of it is checked by the
        artist or their publisher.
      </p>

      <h2 className="section-heading serif">Words</h2>
      <p>
        Lyrics come from LRCLIB, a community lyrics database, or are transcribed
        from the audio when it has none. Lyrics are the property of their writers
        and publishers. They are shown here to make a chord chart usable, in the
        same way a songbook prints them, and are not offered for any other use.
      </p>

      <h2 className="section-heading serif">Your account</h2>
      <p>
        An account exists so setlists can be yours and corrections can carry a
        name. Corrections and chart edits are public and are credited to the name
        on your profile. Setlists are private unless you share the link.
      </p>
      <p>
        Requesting a new song spends real processing time on a small machine, so
        it is limited to twenty songs an hour per account. Automated or bulk use
        is not welcome and will be cut off.
      </p>

      <h2 className="section-heading serif">No warranty</h2>
      <p>
        The service is provided as it is, with no promises about accuracy,
        availability or fitness for anything in particular. If it breaks during
        your gig, that is genuinely bad luck and not something you can claim for.
      </p>

      <h2 className="section-heading serif">Getting in touch</h2>
      <p>
        Rights holders who want a song removed, and anyone else with a problem,
        can write to <a href="mailto:hello@everychord.net">hello@everychord.net</a>.
        Removal requests are acted on without argument.
      </p>

      <p className="muted small">
        <Link to="/privacy">Privacy</Link> · <Link to="/">Back to songs</Link>
      </p>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="page narrow legal">
      <h1 className="serif hero-small">Privacy</h1>
      <p className="muted small">Last updated 24 August 2026</p>

      <h2 className="section-heading serif">What is kept</h2>
      <ul>
        <li><b>Your email address</b>, because sign-in works by sending a link to it.</li>
        <li><b>A display name and handle</b>, if you enter them, to credit your corrections.</li>
        <li><b>Your setlists</b>, and the songs in them.</li>
        <li><b>Corrections and chart edits you make</b>, with your account attached.</li>
      </ul>
      <p>That is the complete list. Nothing else about you is stored.</p>

      <h2 className="section-heading serif">What is not</h2>
      <p>
        No analytics, no advertising, no tracking pixels, no third-party scripts
        beyond the YouTube player on a song page and Google Fonts for the
        typefaces. Nothing is sold or shared, because there is nobody to sell it
        to and no reason to.
      </p>

      <h2 className="section-heading serif">Where it lives</h2>
      <p>
        Accounts and setlists are in a Supabase database in Frankfurt. Chord
        analyses are files on Cloudflare R2. Audio is fetched, analysed and
        deleted within the minute — no recordings are kept.
      </p>

      <h2 className="section-heading serif">Songs you play</h2>
      <p>
        A song page loads a YouTube player, and YouTube will see that request as
        it would on any site embedding a video. Their terms and their data
        collection apply to that, not ours.
      </p>

      <h2 className="section-heading serif">Getting your data, or removing it</h2>
      <p>
        <Link to="/settings">Your account</Link> has a button that downloads
        everything you have made as one file. To delete the account, write to{" "}
        <a href="mailto:hello@everychord.net">hello@everychord.net</a>. Setlists go
        with it; corrections stay but stop carrying your name, because other
        people's charts depend on them.
      </p>

      <p className="muted small">
        <Link to="/terms">Terms</Link> · <Link to="/">Back to songs</Link>
      </p>
    </div>
  );
}
